/* import __COLOCATED_TEMPLATE__ from './change-conversation-priority.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import { type MacroActionComponentArgs } from 'embercom/lib/inbox2/bulk-action-types';
import {
  PRIORITY_LEVELS,
  PRIORITY_LEVEL_PICKER_ORDER,
  priorityLevelDisplay,
  type PriorityLevelName,
} from 'embercom/lib/inbox/priority-levels';

interface Signature {
  Element: HTMLElement;
  Args: MacroActionComponentArgs;
}

// Static i18n keys per level — the ember skill forbids template-literal key
// construction. Same map shape as the Command-K picker uses; the keys are
// shared translation entries.
const LEVEL_LABEL_KEYS: Record<PriorityLevelName, string> = {
  none: 'inbox.command-k.set-priority-level.none',
  low: 'inbox.command-k.set-priority-level.low',
  medium: 'inbox.command-k.set-priority-level.medium',
  high: 'inbox.command-k.set-priority-level.high',
  urgent: 'inbox.command-k.set-priority-level.urgent',
};

// Macro action UI for "Change priority". When the multi-level flag is on the
// action stores a numeric `priority` in `macroAction.data` and renders an
// IcSelect with all five levels — mirroring `set-ticket-custom-state`. With
// the flag off we keep the legacy template-only render (filled star, no
// picker), which preserves the binary "marks as priority" semantics that
// existing macros depend on.
export default class ChangeConversationPriority extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;

  get isMultiLevelEnabled() {
    return this.session.workspace.isFeatureEnabled('team-inbox-priority-levels');
  }

  get priorityLevels() {
    return PRIORITY_LEVEL_PICKER_ORDER.map((value) => {
      let display = priorityLevelDisplay(value);
      return {
        value,
        text: this.intl.t(LEVEL_LABEL_KEYS[display.name]),
      };
    });
  }

  // Existing macros saved before this change have no `priority` field — they
  // implicitly mean "Mark as priority" (urgent). We default the picker to
  // urgent in that case so the action stays valid.
  get selectedPriority(): number {
    let stored = this.args.macroAction.data?.priority;
    return typeof stored === 'number' ? stored : PRIORITY_LEVELS.PRIORITY;
  }

  get label() {
    let display = priorityLevelDisplay(this.selectedPriority);
    return this.intl.t(LEVEL_LABEL_KEYS[display.name]);
  }

  get isInvalid() {
    if (this.args.forceInvalid) {
      return true;
    }
    return this.args.macroAction.isInvalid;
  }

  @action onSelectPriority(priority: number) {
    this.args.macroAction.updateData({ priority });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::MacroActions::ChangeConversationPriority': typeof ChangeConversationPriority;
    'inbox2/composer/macro-actions/change-conversation-priority': typeof ChangeConversationPriority;
  }
}
